import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Modal,
  ModalBody,
  ModalFooter,
  Row,
  ModalHeader,
} from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import axiosInstance from "../../components/AxiosInstance";

const Compressor = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [edited, setEdited] = useState(null);
  const [responseMessage, setResponseMessage] = useState("");
  const [compressorData, setCompressorData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const authToken = localStorage.getItem("access");
  const companyIdLocal = localStorage.getItem("companyid");
  const machineId = localStorage.getItem("selectedMachineId")
  // form
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    index: "",
    set_point:"",
    image: null,
    id: "",
    is_deleted: false,
  });
  
   useEffect(() => {
      fetchCompressors();
    }, []);

  const fetchCompressors = async () => {
    try {
     
      const url = `https://api.hvacsync.in/api/machines/${machineId}/compressors/`;
      const res = await axiosInstance(url,
        {headers: {
         "Content-Type": "application/json",
         Authorization: `Bearer ${authToken}`, 
        },}
      );
   
      setCompressorData(res?.data?.data || []);
    } catch (err) {
      console.error(err);
  };
  }
  // open/close modal and set edited
  const toggleModal = () => {
    setModalOpen(!modalOpen);
    if (!modalOpen) {
      // opening -> nothing
    } else {
      // closing -> clear
      setEdited(null);
      resetForm();
    }
  };

  const openAdd = () => {
    setEdited(null);
    resetForm();
    setModalOpen(true);
  };

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      index: "",
      set_point:"",
      is_deleted: false,
      image: null,
      id: "",
    });
    setResponseMessage("");
  };

  // when editing, populate form
  useEffect(() => {
    if (edited) {
      setFormData({
        name: edited.name || "",
        description: edited.description || "",
        index: edited.index ?? "",
        set_point:edited.set_point ?? "",
        image: edited.image || null,
        id: edited.id || "",
        is_deleted: edited.is_deleted || "",
      });
      setModalOpen(true);
    }
  }, [edited]);

  // form change
  const handleInputChange = (e) => {
    const { id, value, type, checked, files } = e.target;
    if (type === "file") {
      setFormData((p) => ({ ...p, image: files[0] }));
    } else if (type === "checkbox") {
      setFormData((p) => ({ ...p, [id]: checked }));
    } else {
      setFormData((p) => ({ ...p, [id]: value }));
    }
  };

  // submit create/update
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const apiUrl = formData.id
        ? `https://api.hvacsync.in/api/compressors/${formData.id}/`
        : `https://api.hvacsync.in/api/machines/${machineId}/compressors/`;
      const method = formData.id ? "PATCH" : "POST";

      const body = new FormData();
      body.append("name", formData.name);
      
      body.append("description", formData.description ?? "");
      
      if (formData.index !== "") body.append("index", formData.index);
      if (formData.image && typeof formData.image !== "string") {
        body.append("image", formData.image);
      }

      const headers = {};
      if (authToken) headers["Authorization"] = `Bearer ${authToken}`;

      const res = await axiosInstance(apiUrl, {
        method,
        headers,
        body,
      });
      if (res.ok) {
        toast.success(res.message || "Compressor saved successfully");
        setModalOpen(false);
        setEdited(null);
        resetForm();
        fetchCompressors();
      } else {
        console.error("submit error", res);
        toast.error(
          res?.message ||
            `Error saving compressor: ${JSON.stringify(res)}`
        );
      }
    } catch (err) {
      console.error(err);
      toast.error("Unexpected error occurred");
    }
  };

  // delete
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this compressor?")) return;
    try {
      const headers = {};
      if (authToken) headers["Authorization"] = `Bearer ${authToken}`;

      const res = await axiosInstance (`https://api.hvacsync.in/api/compressors/${id}/`, {
        method: "DELETE",
        headers,
      });

      if (res.ok) {
        toast.success("Compressor deleted successfully");
        fetchCompressors();
      } else if (res.status === 401) {
        localStorage.removeItem("authUser");
        localStorage.removeItem("admin");
      } else {
        const data = await res.text();
        console.error("delete error", data);
        toast.error("Failed to delete compressor");
      }
    } catch (err) {
      console.error(err);
      toast.error("Error deleting compressor");
    }
  };

  // search + pagination
  const filtered = compressorData.filter((c) =>
    (c.name || "").toLowerCase().includes(searchTerm.toLowerCase())
  );

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;
  const totalPages = Math.max(1, Math.ceil(filtered.length / itemsPerPage));
  const indexOfLast = currentPage * itemsPerPage;
  const indexOfFirst = indexOfLast - itemsPerPage;
  const currentItems = filtered.slice(indexOfFirst, indexOfLast);

  const handlePrevious = () => setCurrentPage((p) => Math.max(1, p - 1));
  const handleNext = () => setCurrentPage((p) => Math.min(totalPages, p + 1));

  // small helper to render image (if API returns path or filename)
  const getImageSrc = (img) =>
    img ? `https://api.hvacsync.in/media/${img}` : "https://img.icons8.com/ios-filled/100/CCCCCC/settings.png";

  document.title = "Compressors | HVACSYNC";

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="HVACSYNC" breadcrumbItem="Compressor" />

          <Row>
            <Col lg={12}>
              <Card>
                <CardHeader>
                  <h4 className="card-title mb-0">Add, Edit & Remove Compressor</h4>
                </CardHeader>

                <CardBody>
                  <div id="compressorList">
                    <Row className="g-4 mb-3">
                      <Col className="col-sm-auto">
                        <div className="d-flex gap-1">
                          <Button color="success" className="add-btn" onClick={openAdd} id="create-btn">
                            <i className="ri-add-line align-bottom me-1"></i> Add New Compressor
                          </Button>
                        </div>
                      </Col>
                      <Col className="col-sm">
                        <div className="d-flex justify-content-sm-end">
                          <div className="search-box ms-2">
                            <input
                              type="text"
                              className="form-control search"
                              placeholder="Search..."
                              value={searchTerm}
                              onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                            />
                          </div>
                        </div>
                      </Col>
                    </Row>

                    <div className="table-responsive table-card mt-3 mb-1">
                      <table className="table align-middle table-nowrap">
                        <thead className="table-light">
                          <tr>
                            <th>Logo</th>
                            <th>Compressor Name</th>
                            <th>Set Point</th>
                           
                            <th>Index</th>
                          
                            <th>Action</th>
                          </tr>
                        </thead>

                        {isLoading ? (
                          <tbody>
                            <tr>
                              <td colSpan="9" className="text-center">
                                <div className="spinner-border text-primary" role="status">
                                  <span className="visually-hidden">Loading...</span>
                                </div>
                              </td>
                            </tr>
                          </tbody>
                        ) : (
                          <tbody>
                            {currentItems.length === 0 ? (
                              <tr>
                                <td colSpan="9" className="text-center">No content found</td>
                              </tr>
                            ) : (
                              currentItems.map((c, idx) => (
                                <tr key={c.id || idx}>
                                  <td>
                                    <div className="logo">
                                      <img src={getImageSrc(c.image)} alt="compressor" height="48" />
                                    </div>
                                  </td>
                                  <td>{c.name}</td>
                                 <td>{c.set_point}</td>
                                  <td>{c.index ?? "-"}</td>
                                  
                                  <td>
                                    <div className="d-flex gap-2">
                                      <button
                                        className="btn btn-warning"
                                        onClick={() => setEdited(c)}
                                      >
                                        Edit
                                      </button>

                                      <button
                                        className="btn btn-danger"
                                        onClick={() => handleDelete(c.id)}
                                      >
                                        Remove
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        )}
                      </table>
                    </div>

                    <div className="d-flex justify-content-end">
                      <div className="pagination-wrap hstack gap-2">
                        <Link
                          className={`page-item pagination-prev ${currentPage === 1 ? "disabled" : ""}`}
                          to="#"
                          onClick={(e) => { e.preventDefault(); handlePrevious(); }}
                        >
                          Previous
                        </Link>

                        <ul className="pagination listjs-pagination mb-0">
                          <li>Page {currentPage} of {totalPages}</li>
                        </ul>

                        <Link
                          className={`page-item pagination-next ${currentPage === totalPages ? "disabled" : ""}`}
                          to="#"
                          onClick={(e) => { e.preventDefault(); handleNext(); }}
                        >
                          Next
                        </Link>
                      </div>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Add / Edit Modal */}
      <Modal isOpen={modalOpen} toggle={toggleModal} centered>
        <ModalHeader className="bg-light p-3" toggle={toggleModal}>
          {edited ? "Edit Compressor" : "Add Compressor"}
        </ModalHeader>

        {responseMessage && (
          <div className={`alert ${responseMessage.includes("Error") ? "alert-danger" : "alert-success"}`}>
            {responseMessage}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <ModalBody>
            <div className="mb-3" style={{ display: "none" }}>
              <label className="form-label">ID</label>
              <input type="text" readOnly value={formData.id} className="form-control" />
            </div>

    

            <div className="mb-3">
              <label className="form-label">Compressor Name</label>
              <input id="name" className="form-control" value={formData.name} onChange={handleInputChange} required />
            </div>

            <div className="mb-3">
              <label className="form-label">Description</label>
              <input id="description" className="form-control" value={formData.description} onChange={handleInputChange} />
            </div>
           

            <div className="mb-3">
              <label className="form-label">Index</label>
              <input id="index" type="number" className="form-control" value={formData.index} onChange={handleInputChange} />
            </div>

            <div className="mb-3">
              <label className="form-label">Image</label>
              <input id="image" type="file" className="form-control" onChange={handleInputChange} />
              {edited && edited.image && typeof edited.image === "string" && (
                <div className="mt-2">
                  <img src={getImageSrc(edited.image)} height="50" alt="existing" />
                </div>
              )}
            </div>
          </ModalBody>

          <ModalFooter>
            <div className="hstack gap-2 justify-content-end">
              <button type="button" className="btn btn-light" onClick={() => { setModalOpen(false); setEdited(null); resetForm(); }}>Close</button>
              <button type="submit" className="btn btn-success">
                {edited ? "Edit Compressor" : "Add Compressor"}
              </button>
            </div>
          </ModalFooter>
        </form>
      </Modal>
    </React.Fragment>
  );
};

export default Compressor;
